import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Modal,
  ModalContent,
  ModalOverlay,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  Text,
  Box,
  Flex,
} from '@chakra-ui/react';
import styled from 'styled-components';
import Breakpoints from '../../design-utils/breakpoints';
import { cleanDefaultDisplayName } from '../../../components/Subscriptions/utils';
import { getOptimizedImageURL } from '../utils';
import { useGlobalContext } from '../state/GlobalContext';
import { getIdFromGid } from '../../../utils/gid-utils';

interface IProps {
  setOpen: (value: boolean) => void;
  open: boolean;
  loading: boolean;
  handleSkip: (lineId: string, productId: string) => void;
  handleUnskip: (lineId: string, productId: string) => void;
  shuffleData: {
    skipAllowed: boolean;
    upcomingProducts: ShuffleNextProduct[];
    remainingProducts: ShuffleNextProduct[];
    previousProducts: ShuffleNextProduct[];
  };
  lineId: string;
}

export default ({
  open,
  setOpen,
  loading,
  handleSkip,
  handleUnskip,
  shuffleData,
  lineId,
}: IProps): React.ReactElement => {
  const { t } = useTranslation();
  const { enableShuffleSkipProducts } = useGlobalContext();

  const shouldHideSkipButton =
    !enableShuffleSkipProducts ||
    !shuffleData.skipAllowed ||
    !shuffleData.remainingProducts.length;

  const shouldHideUnskipButton = !enableShuffleSkipProducts;

  const onClose = () => setOpen(false);

  return (
    <Modal isOpen={open} onClose={onClose} isCentered>
      <ModalOverlay className="awt-curate-shuffle-modal--overlay">
        <ModalContent className="awt-curate-shuffle-modal">
          <ModalHeader>{t('tables.curate_upcoming_products')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box>
              <Text>{t('tables.next_order_will_contain')}</Text>
              {shuffleData.upcomingProducts.length > 0 ? (
                shuffleData.upcomingProducts.map(
                  getProductRow(
                    shouldHideSkipButton,
                    loading,
                    handleSkip,
                    'skip',
                    lineId,
                    t,
                  ),
                )
              ) : (
                <SubduedText>{t('tables.no_items_available')}</SubduedText>
              )}
            </Box>
            <Box>
              <Text>{t('tables.up_next')}</Text>
              {shuffleData.remainingProducts.length > 0 ? (
                shuffleData.remainingProducts.map(
                  getProductRow(
                    shouldHideSkipButton,
                    loading,
                    handleSkip,
                    'skip',
                    lineId,
                    t,
                  ),
                )
              ) : (
                <SubduedText>{t('tables.no_items_available')}</SubduedText>
              )}
            </Box>
            <Box>
              <Text>{t('tables.skipped_or_delivered')}</Text>
              {shuffleData.previousProducts.length > 0 ? (
                shuffleData.previousProducts.map(
                  getProductRow(
                    shouldHideUnskipButton,
                    loading,
                    handleUnskip,
                    'restore',
                    lineId,
                    t,
                  ),
                )
              ) : (
                <SubduedText>{t('tables.no_items_available')}</SubduedText>
              )}
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button mr="4" onClick={onClose} variant="outlined">
              {t('tables.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  );
};

const getProductRow =
  (
    shouldHideSkipButton: boolean,
    loading: boolean,
    handleClick: (lineId: string, productId: string) => void,
    buttonLabel: string,
    lineId: string,
    t: any,
  ) =>
  ({
    productTitle,
    productId,
    variantTitle,
    variantImage,
    variantId: variantGid,
  }: ShuffleNextProduct) => {
    const variantId = getIdFromGid(variantGid);
    return (
      <Flex
        style={{ padding: '8px', justifyContent: 'space-between' }}
        className={`awt-shuffle-product-row-${variantId}`}
      >
        <BundleItemContainer>
          <BundleImageContainer>
            {variantImage ? (
              <BundleVariantImage
                alt={cleanDefaultDisplayName(
                  `${productTitle} - ${variantTitle}`,
                )}
                src={getOptimizedImageURL({
                  url: variantImage,
                  width: '128',
                })}
              />
            ) : (
              <ImageEmptyState width="32px" height="32px" />
            )}
          </BundleImageContainer>
          <BundleProductLineName>
            {cleanDefaultDisplayName(`${productTitle} - ${variantTitle}`)}
          </BundleProductLineName>
        </BundleItemContainer>

        {!shouldHideSkipButton && (
          <SkipButton
            variant="outlined"
            className="awt-shuffle-skip-btn"
            disabled={loading}
            onClick={() => handleClick(lineId, productId)}
          >
            {t(`tables.${buttonLabel}`)}
          </SkipButton>
        )}
      </Flex>
    );
  };

const BundleItemContainer = styled.div`
  display: flex;
  padding-left: 12px;
  align-items: center;
`;

const BundleProductLineName = styled.h3.attrs({
  className: 'awt-shuffle-product-name',
})`
  //on mobile screen
  @media (max-width: ${Breakpoints.sm}) {
    font-size: 14px;
  }
  font-size: 16px;
  display: flex;
  align-items: center;
  font-weight: 700;
  margin-bottom: 2px;
`;

const BundleImageContainer = styled.div.attrs({
  className: 'awt-img-container',
})`
  position: relative;
  padding-right: 16px;
  display: inline-block;
`;

const BundleVariantImage = styled.img.attrs({
  className: 'awt-shuffle-variant-img',
})`
  width: 32px;
  border-radius: 8px;
  position: relative;
  min-width: 32px;
`;

const ImageEmptyState = styled.div.attrs({
  className: 'shuffle-img-empty-state',
})<{ width?: string; height?: string }>`
  width: ${({ width }) => width ?? '64px'};
  height: ${({ height }) => height ?? '64px'};
  border-radius: 8px;
  background-color: #dfe3e8;
  position: relative;
  min-width: 32px;
  &:empty {
    display: block;
  }
`;

const SkipButton = styled(Button)`
  padding: 0;
  height: 26px;
`;

const SubduedText = styled(Text)`
  color: gray !important;
  font-size: 14px !important;
`;
